export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: "dashboard.view",

  // Properties
  PROPERTIES_VIEW: "properties.view",
  PROPERTIES_EDIT: "properties.edit",
  PROPERTIES_AVM_OVERRIDE: "properties.avm_override",
  PROPERTIES_BULK: "properties.bulk",
  PROPERTIES_QUALITY_REVIEW: "properties.quality_review",
  PROPERTIES_REFRESH: "properties.refresh",
  // Reveal of owner contact details (PII). Restricted to Super Admin,
  // Data Admin, and Analyst roles.
  PROPERTY_OWNERS_VIEW: "property_owners.view",

  // Clients
  CLIENTS_VIEW: "clients.view",
  CLIENTS_EDIT: "clients.edit",
  CLIENTS_PIPELINE: "clients.pipeline",
  CLIENTS_EXPORT: "clients.export",

  // Agents
  AGENTS_VIEW: "agents.view",
  AGENTS_EDIT: "agents.edit",
  AGENTS_COMMISSION: "agents.commission",

  // Content
  CONTENT_VIEW: "content.view",
  CONTENT_EDIT: "content.edit",
  CONTENT_PUBLISH: "content.publish",

  // Newsletter
  NEWSLETTER_VIEW: "newsletter.view",
  NEWSLETTER_EDIT: "newsletter.edit",
  NEWSLETTER_APPROVE: "newsletter.approve",
  NEWSLETTER_SEND: "newsletter.send",

  // Data Quality
  DATA_QUALITY_VIEW: "data_quality.view",
  DATA_QUALITY_MANAGE: "data_quality.manage",
  DATA_QUALITY_SCORECARD: "data_quality.scorecard",
  DATA_QUALITY_COMPLETENESS: "data_quality.completeness",
  DATA_QUALITY_ANOMALIES: "data_quality.anomalies",
  DATA_QUALITY_DEDUP: "data_quality.dedup",

  // AI Models
  MODELS_VIEW: "models.view",
  MODELS_MANAGE: "models.manage",
  MODELS_RETRAIN: "models.retrain",
  MODELS_ACCURACY: "models.accuracy",
  MODELS_DRIFT: "models.drift",
  MODELS_FEATURES: "models.features",
  MODELS_AB_TEST: "models.ab_test",
  MODELS_BACKTEST: "models.backtest",
  MODELS_CALIBRATION: "models.calibration",

  // Billing
  BILLING_VIEW: "billing.view",
  BILLING_REVENUE: "billing.revenue",
  BILLING_MANAGE: "billing.manage",

  // Settings / System
  SETTINGS_VIEW: "settings.view",
  SETTINGS_MANAGE: "settings.manage",
  SETTINGS_DEAL_SCORE: "settings.deal_score",
  SETTINGS_SCRAPER: "settings.scraper",
  SETTINGS_PLATFORM: "settings.platform",
  SETTINGS_CACHE: "settings.cache",
  SETTINGS_HEALTH: "settings.health",
  SETTINGS_FLAGS: "settings.flags",

  // Platform Users
  PLATFORM_USERS_VIEW: "platform_users.view",
  PLATFORM_USERS_MANAGE: "platform_users.manage",

  // Admin Users
  USERS_VIEW: "users.view",
  USERS_MANAGE: "users.manage",
  ROLES_MANAGE: "roles.manage",

  // Alerts
  ALERTS_VIEW: "alerts.view",
  ALERTS_MANAGE: "alerts.manage",

  // Audit
  AUDIT_VIEW: "audit.view",
  AUDIT_EXPORT: "audit.export",

  // Platform Logs
  LOGS_VIEW: "logs.view",

  // Vehicles
  VEHICLES_VIEW: "vehicles.view",

  // Leads
  LEADS_VIEW: "leads.view",
  LEADS_EDIT: "leads.edit",
  LEADS_ASSIGN: "leads.assign",
  LEADS_PIPELINE: "leads.pipeline",
  LEADS_COMPLIANCE: "leads.compliance",

  // Property Lookup (Apify Unit Number + Owner Finder)
  PROPERTY_LOOKUP_VIEW: "property_lookup.view",
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Role-to-permissions mapping per SPEC §8.3 */
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  super_admin: Object.values(PERMISSIONS),
  // Data Admin — a super admin MINUS administering other admins
  // (MPPI-00711). Defined as "everything except X", like `analyst` below, so
  // the role tracks new permissions automatically. The hand-written list this
  // replaces had drifted to 31 entries and was missing `leads.view`, which
  // made the API widening invisible: the guards allowed it and the menu
  // never showed it (pattern 49, and pattern 52 — a control wired to an
  // empty source).
  //
  // Menu visibility is a convenience; the API guard is the access control.
  // Anything platform-only stays closed to a tenant data admin regardless of
  // what is listed here, because scope is a separate axis from role.
  data_admin: Object.values(PERMISSIONS).filter(
    (p) =>
      p !== "users.view" && // list / open another admin
      p !== "users.manage" && // admin user create / edit / deactivate
      p !== "roles.manage", // assign roles to admin users
  ),
  // Data Analyst — broad surface area, near-Super Admin minus the
  // three carve-outs the operator flagged: admin-user CRUD, platform
  // settings, and admin-role management. Defined as "everything except
  // X" so the role tracks new permission additions automatically.
  analyst: Object.values(PERMISSIONS).filter(
    (p) =>
      p !== "users.manage" && // admin user create / edit / delete
      p !== "roles.manage" && // assign roles to admin users
      p !== "settings.manage" && // top-level platform settings mutate
      p !== "settings.platform", // Cognito / env / retention etc.
  ),
  agent: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.PROPERTIES_VIEW,
    PERMISSIONS.CLIENTS_VIEW,
    PERMISSIONS.CLIENTS_EDIT,
    PERMISSIONS.CLIENTS_PIPELINE,
    PERMISSIONS.AGENTS_VIEW,
    PERMISSIONS.ALERTS_VIEW,
    PERMISSIONS.LEADS_VIEW,
    PERMISSIONS.LEADS_EDIT,
    PERMISSIONS.LEADS_PIPELINE,
  ],
  compliance_officer: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.AUDIT_VIEW,
    PERMISSIONS.AUDIT_EXPORT,
    PERMISSIONS.PLATFORM_USERS_VIEW,
    PERMISSIONS.ALERTS_VIEW,
    PERMISSIONS.DATA_QUALITY_VIEW,
    PERMISSIONS.DATA_QUALITY_SCORECARD,
    PERMISSIONS.LEADS_VIEW,
    PERMISSIONS.LEADS_COMPLIANCE,
    PERMISSIONS.NEWSLETTER_VIEW,
    PERMISSIONS.NEWSLETTER_EDIT,
    PERMISSIONS.CONTENT_VIEW,
    PERMISSIONS.CONTENT_EDIT,
  ],
};
